import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { auth } from "../../backend/firebaseConfig";
import "../../css/StaffHoliday/StaffHoliday.css";

import Sidebar from "../DesignSystem/Sidebar";
import Breadcrumb from "../DesignSystem/Breadcrumb/Breadcrumb";
import SummaryCards from "../DesignSystem/SummaryCards/SummaryCards";
import RadioGroup from "../DesignSystem/SelectionControls/RadioGroup";

import { useUserPermissions } from "../SignIn/UserPermissionsContext";
import { useStaffHolidayProfile } from "./hooks/useStaffHolidayProfile";
import { useStaffHolidayRequests } from "./hooks/useStaffHolidayRequests";
import {
  LEAVE_STATUS_LABELS,
  LEAVE_TYPE_LABELS,
} from "./utils/staffHolidayConstants";
import { hasHolidayAdminPermission } from "./utils/staffHolidayPermissions";

import { formatDisplayDate } from "./utils/staffHolidayDateUtils";
import { useCurrentUserEmail } from "./hooks/useCurrentUserEmail";
import StatusButton from "../DesignSystem/Buttons/StatusButton";
import { cancelStaffHolidayRequest } from "./services/staffHolidayFirebase";
import StaffHolidayActionConfirmModal from "./components/StaffHolidayActionConfirmModal";
import {
  buildLeaveStatCards,
  getAnnualLeaveStats,
  getLatestLeaveHistoryEntries,
} from "./utils/staffHolidayStaffUtils";
import { Helmet } from "react-helmet-async";

const currentYear = new Date().getFullYear();

const requestOverlapsYear = (request = {}, year) => {
  const yearStart = `${year}-01-01`;
  const yearEnd = `${year}-12-31`;
  const startDate = String(request.startDate || request.date || "").slice(0, 10);
  const endDate = String(
    request.endDate || request.startDate || request.date || "",
  ).slice(0, 10);

  return Boolean(startDate && endDate && startDate <= yearEnd && endDate >= yearStart);
};

const StaffHolidayDashboard = () => {
  const currentUserEmail =
    auth.currentUser?.email || localStorage.getItem("userEmail") || "";
  const [cancelModal, setCancelModal] = useState({
    open: false,
    request: null,
    reason: "",
  });
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const permissionsContext = useUserPermissions?.() || {};
  const isHolidayAdmin = hasHolidayAdminPermission(
    permissionsContext.userPermissions,
  );

  const { profile, balance } = useStaffHolidayProfile(
    currentUserEmail,
    selectedYear,
  );

  const { requests, error: myRequestsError } = useStaffHolidayRequests({
    currentUserEmail,
    mode: "mine",
  });

  const {
    requests: adminRequests,
    loading: adminRequestsLoading,
    error: adminRequestsError,
  } = useStaffHolidayRequests({
    currentUserEmail,
    mode: isHolidayAdmin ? "admin" : "mine",
  });

 const getRequestSortDate = (request = {}) => {
   const value = request.startDate || request.date || request.endDate;

   if (!value) return 0;

   if (typeof value?.toDate === "function") {
     return value.toDate().getTime();
   }

   const stringValue = String(value);
   const isoMatch = stringValue.match(/^(\d{4})-(\d{2})-(\d{2})/);

   if (isoMatch) {
     const [, year, month, day] = isoMatch;
     return new Date(Number(year), Number(month) - 1, Number(day)).getTime();
   }

   const date = new Date(value);

   return Number.isNaN(date.getTime()) ? 0 : date.getTime();
 };

 const latestRequests = useMemo(() => {
   return getLatestLeaveHistoryEntries(requests)
     .filter((request) => requestOverlapsYear(request, selectedYear))
     .sort((a, b) => getRequestSortDate(b) - getRequestSortDate(a))
     .slice(0, 50);
 }, [requests, selectedYear]);

  const requestsByUser = useMemo(() => {
    if (!isHolidayAdmin) {
      return [];
    }

    const grouped = new Map();

    getLatestLeaveHistoryEntries(adminRequests).forEach((request) => {
      const userEmail =
        request.staffEmail || request.ownerStaffEmail || "unknown-user";
      const userName =
        request.staffName ||
        request.displayName ||
        request.staffEmail ||
        userEmail;

      if (!grouped.has(userEmail)) {
        grouped.set(userEmail, {
          userEmail,
          userName,
          requests: [],
        });
      }

      grouped.get(userEmail).requests.push(request);
    });

    return Array.from(grouped.values())
      .map((group) => ({
        ...group,
        requests: group.requests.slice(0, 10),
      }))
      .sort((a, b) => String(a.userName).localeCompare(String(b.userName)));
  }, [adminRequests, isHolidayAdmin]);

  const summaryCards = useMemo(() => {
    const stats = getAnnualLeaveStats({
      staff: {
        ...(profile || {}),
        holidayBalance: balance,
        leaveBalance: balance,
        balance,
      },
      requests,
      selectedYear,
    });

    return buildLeaveStatCards(stats);
  }, [balance, profile, requests, selectedYear]);
  const navigate = useNavigate();

  const [actionLoadingId, setActionLoadingId] = useState("");
  const [actionError, setActionError] = useState("");

  const getRequestId = (request = {}) => request.requestId || request.id;

  const isPendingRequest = (status = "") => {
    return [
      "pending_manager",
      "pending_holiday_manager",
      "pending_admin",
    ].includes(String(status).toLowerCase());
  };

  const getDateOnly = (value) => {
    if (!value) return null;

    if (typeof value?.toDate === "function") {
      const date = value.toDate();
      return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    }

    const stringValue = String(value);
    const isoMatch = stringValue.match(/^(\d{4})-(\d{2})-(\d{2})/);

    if (isoMatch) {
      const [, year, month, day] = isoMatch;
      return new Date(Number(year), Number(month) - 1, Number(day));
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return null;

    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  };

  const getTodayDateOnly = () => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), today.getDate());
  };

  const isPastRequest = (request = {}) => {
    const requestEndDate = getDateOnly(request.endDate || request.startDate);

    if (!requestEndDate) return false;

    return requestEndDate < getTodayDateOnly();
  };

  const canCancelRequest = (request = {}) => {
    const status = String(request.status || "").toLowerCase();

    if (["cancelled", "rejected"].includes(status)) {
      return false;
    }

    return !isPastRequest(request);
  };

  const handleEditRequest = (request) => {
    const requestId = getRequestId(request);

    if (!requestId) return;

    navigate(`/staff-holiday/request?editRequestId=${requestId}`);
  };

  const openCancelModal = (request) => {
    setActionError("");

    if (!canCancelRequest(request)) {
      setActionError(
        "You cannot cancel a request after the leave date has passed.",
      );
      return;
    }

    setCancelModal({
      open: true,
      request,
      reason: "",
    });
  };

  const closeCancelModal = () => {
    if (actionLoadingId) return;

    setCancelModal({
      open: false,
      request: null,
      reason: "",
    });
  };

  const updateCancelReason = (reason) => {
    setCancelModal((prev) => ({
      ...prev,
      reason,
    }));
  };

  const confirmCancelRequest = async () => {
    const request = cancelModal.request;
    const requestId = getRequestId(request);

    if (!requestId || !request?.staffEmail) {
      setActionError(
        "Could not cancel this request because the request details are missing.",
      );
      closeCancelModal();
      return;
    }

    setActionError("");
    setActionLoadingId(requestId);

    try {
      await cancelStaffHolidayRequest({
        staffEmail: request.staffEmail,
        requestId,
        cancelledBy: currentUserEmail,
        cancellationReason:
          cancelModal.reason.trim() || `Cancelled by ${currentUserEmail}.`,
      });

      setCancelModal({
        open: false,
        request: null,
        reason: "",
      });
    } catch (error) {
      setActionError(error.message || "Failed to cancel holiday request.");
    } finally {
      setActionLoadingId("");
    }
  };

  // useEffect(() => {
  //   console.log("[StaffHolidayDashboard] dataSnapshot", {
  //     currentUserEmail,
  //     isHolidayAdmin,
  //     profile,
  //     balance,
  //     myRequestsCount: requests.length,
  //     myRequestsError: myRequestsError?.message || null,
  //     adminRequestsCount: adminRequests.length,
  //     adminRequestsLoading,
  //     adminRequestsError: adminRequestsError?.message || null,
  //     groupedUsersCount: requestsByUser.length,
  //     groupedUsers: requestsByUser,
  //   });
  // }, [
  //   currentUserEmail,
  //   isHolidayAdmin,
  //   profile,
  //   balance,
  //   requests,
  //   myRequestsError,
  //   adminRequests,
  //   adminRequestsLoading,
  //   adminRequestsError,
  //   requestsByUser,
  // ]);

  // if (authLoading) {
  //   return (
  //     <div className="Main-Page">
  //       <Sidebar />
  //       <div className="Main-Container">
  //         <main className="staff-holiday-page">
  //           <p className="FP_Body grey">Loading holiday dashboard...</p>
  //         </main>
  //       </div>
  //     </div>
  //   );
  // }

  const getHolidayStatusTone = (status = "") => {
    const normalizedStatus = String(status).toLowerCase();

    const statusToneMap = {
      pending_manager: "yellow",
      pending_holiday_manager: "yellow",
      pending_admin: "purple",
      approved: "green",
      rejected: "red",
      cancelled: "grey",
    };

    return statusToneMap[normalizedStatus] || "grey";
  };

  return (
    <div className="Main-Page">
      <Helmet>
        <title>Holiday Dashboard</title>
      </Helmet>
      <Sidebar />

      <div className="Main-Container">
        <main className="staff-holiday-page">
          <Breadcrumb
            items={[
              { label: "All Staff", to: "/Department/AllStaff" },
              { label: "My Holiday" },
            ]}
          />

          <div className="staff-holiday-header">
              <h3 className="FP_H3 primary-text">Holiday Dashboard</h3>
         

            <div className="staff-holiday-header-actions">
              <Link
                to="/staff-holiday/request"
                className="staff-holiday-primary-link"
              >
                Request Leave
              </Link>

              <Link
                to="/staff-holiday/tracker"
                className="staff-holiday-secondary-link"
              >
                View Tracker
              </Link>

              {isHolidayAdmin && (
                <>
                  <Link
                    to="/staff-holiday/approvals"
                    className="staff-holiday-secondary-link"
                  >
                    Approvals
                  </Link>

                  {/* <Link
                    to="/staff-holiday/import"
                    className="staff-holiday-secondary-link"
                  >
                    Upload Holiday Data
                  </Link>  */}
                </>
              )}
            </div>
          </div>

          <div className="staff-holiday-year-selector">
            <RadioGroup
              label="Holiday year"
              name="holiday-dashboard-year"
              options={[
                { value: currentYear, label: String(currentYear) },
                { value: currentYear + 1, label: String(currentYear + 1) },
              ]}
              value={selectedYear}
              onChange={setSelectedYear}
              columns={2}
            />
          </div>

          <SummaryCards cards={summaryCards} grid="4" />

          <div className="staff-holiday-card staff-holiday-recent-card">
            <div className="staff-holiday-card-header">
              <div>
                <div className="FP_Content_Semibold staff-holiday-card-title">
                  My {selectedYear} Requests
                </div>
                <p className="FP_Help_Text grey">
                  Your holiday requests and approval status for {selectedYear}.
                </p>
              </div>
            </div>

            {actionError && (
              <div className="staff-holiday-message staff-holiday-message--error">
                {actionError}
              </div>
            )}

            {latestRequests.length === 0 ? (
              <div className="staff-holiday-empty-state">
                <p className="FP_Help_Text grey">
                  No holiday requests found for {selectedYear}.
                </p>
              </div>
            ) : (
              <div className="staff-holiday-request-list">
                {latestRequests.map((request) => {
                  const requestId = getRequestId(request);

                  const statusLabel =
                    LEAVE_STATUS_LABELS[request.status] ||
                    request.status ||
                    "Unknown";

                  const statusTone = getHolidayStatusTone(request.status);
                  const isPending = isPendingRequest(request.status);
                  const isCancellable = canCancelRequest(request);
                  const isCancelling = actionLoadingId === requestId;

                  return (
                    <div key={requestId} className="staff-holiday-mini-row">
                      <div className="fit-column gap-20">
                        <div className="staff-holiday-mini-row-content">
                          <strong className="FP_Content_Semibold black">
                            {LEAVE_TYPE_LABELS[request.leaveType] ||
                              request.leaveType}
                          </strong>

                          <span className="FP_Content staff-holiday-mini-row-date">
                            {formatDisplayDate(request.startDate)} to{" "}
                            {formatDisplayDate(request.endDate)}
                            {request.staffNotes && ` · ${request.staffNotes}`}
                          </span>
                        </div>
                        <div className="staff-holiday-mini-actions">
                          {isPending && (
                            <button
                              type="button"
                              className="staff-holiday-row-action"
                              onClick={() => handleEditRequest(request)}
                            >
                              Edit
                            </button>
                          )}

                          {/* <Link
                            to={`/staff-holiday/tracker?year=${
                              String(request.startDate || "").slice(0, 4) ||
                              currentYear
                            }`}
                            className="staff-holiday-row-action"
                          >
                            Tracker
                          </Link> */}

                          {isCancellable && (
                            <button
                              type="button"
                              className="staff-holiday-row-action staff-holiday-row-action--danger"
                              onClick={() => openCancelModal(request)}
                              disabled={isCancelling}
                            >
                              {isCancelling ? "Cancelling..." : "Cancel"}
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="staff-holiday-mini-row-right">
                        <StatusButton type={statusLabel} color={statusTone} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          {/* {isHolidayAdmin && (
            <div className="staff-holiday-card">
              <h2 className="FP_H2 staff-holiday-card-title">
                All Users Request History
              </h2>

              {adminRequestsError && (
                <p className="FP_Body grey">
                  Could not load user history:{" "}
                  {adminRequestsError.message || "Unknown error"}
                </p>
              )}

              {adminRequestsLoading ? (
                <p className="FP_Body grey">Loading user request history...</p>
              ) : requestsByUser.length === 0 ? (
                <p className="FP_Body grey">No user request history found.</p>
              ) : (
                <div className="staff-holiday-request-list">
                  {requestsByUser.map((group) => (
                    <div key={group.userEmail} className="staff-holiday-card">
                      <h3 className="FP_H3 primary-text">{group.userName}</h3>
                      <p className="FP_Content grey">{group.userEmail}</p>

                      <div className="staff-holiday-request-list">
                        {group.requests.map((request) => (
                          <div
                            key={`${group.userEmail}-${request.id}`}
                            className="staff-holiday-mini-row"
                          >
                            <div>
                              <strong className="FP_Content_Semibold black">
                                {LEAVE_TYPE_LABELS[request.leaveType] ||
                                  request.leaveType}
                              </strong>

                              <span className="FP_Content black">
                                {formatDisplayDate(request.startDate)} to{" "}
                                {formatDisplayDate(request.endDate)}
                              </span>
                            </div>

                            <span
                              className={`staff-holiday-status ${request.status} FP_Content_Semibold`}
                            >
                              {LEAVE_STATUS_LABELS[request.status] ||
                                request.status}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )} */}
          <StaffHolidayActionConfirmModal
            open={cancelModal.open}
            request={cancelModal.request}
            title="Cancel holiday request?"
            description="This will mark the request as cancelled. If it has already been approved, the backend will reverse the tracker entry and balance."
            confirmLabel="Cancel Request"
            tone="danger"
            busy={Boolean(actionLoadingId)}
            showReason
            reason={cancelModal.reason}
            reasonLabel="Cancellation reason"
            reasonPlaceholder="Optional reason for cancellation"
            onReasonChange={updateCancelReason}
            onConfirm={confirmCancelRequest}
            onClose={closeCancelModal}
          />
        </main>
      </div>
    </div>
  );
};

export default StaffHolidayDashboard;
